import { SearchBar } from "dyl-components";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Grid, Ref, Tab } from "semantic-ui-react";
import CategoryProducts from "./CategoryProducts";
import productCatalogActions from "actions/product_catalog";
import { useController, useFormContext } from "react-hook-form";
import { QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";

const Products = ({ opportunity_id, account_id }) => {
    const { categories } = useSelector((state) => ({
        categories: state.product_category.categories,
    }));

    const account_term = useSelector((state) => {
        const { price_schedule, is_current_price_schedule } = state.subscription.term || { term: null };
        if (is_current_price_schedule) {
            return price_schedule;
        } 
        return null;
    });

    const tabContainerRef = useRef();

    const panes = [
        {
            key: "all",
            menuItem: "All",
        },
        {
            key: "uncategorized",
            menuItem: "Uncategorized",
        },
        ...categories
            ?.filter((category) => category.has_products)
            .map((category) => ({
                key: category.id,
                menuItem: category.name,
            })),
    ];

    const [activeTab, setActiveTab] = useState("all");

    const getActiveTab = () => {
        const index = panes.findIndex(
            (pane) => pane.menuItem?.toLowerCase() === activeTab?.toLowerCase()
        );
        return index === -1 ? 0 : index;
    };

    const onChangeTab = (index) => {
        const { menuItem } = panes[index];
        setActiveTab(menuItem);
    };
    const { control } = useFormContext();

    const { field: masterFilterField } = useController({
        control,
        name: "pricing_schedule"
    });

    const masterFilterValue = masterFilterField?.value;
    const dispatch = useDispatch();

    const [queryParameters, setQueryParameters] = useState({
        page: 1,
        product_type: [],
        product_category_id: null,
        search: "",
        account_id,
        master_filter: masterFilterValue,
    });

    const isDyl = useSelector(state => state.auth.dyl_employee);

    useEffect(() => {
        dispatch(productCatalogActions.readCatalog({
            page: 1,
            product_type: [],
            product_category_id: null,
            search: "",
            master_filter: masterFilterValue,
            account_id,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const searchFunction = (searchValue) => {
        const newQueryParameters = {
            ...queryParameters,
            search: searchValue,
            page: 1
        };
        setQueryParameters(newQueryParameters);
        dispatch(productCatalogActions.readCatalog(newQueryParameters));
    };

    const searchCancelFunction = () => {
        const newQueryParameters = {
            ...queryParameters,
            search: "",
            page: 1
        };
        setQueryParameters(newQueryParameters);
        dispatch(productCatalogActions.readCatalog(newQueryParameters));
    };

    const { quoteBuilderConfig } = useContext(QuoteBuilderContext);
    const { isUpsell } = quoteBuilderConfig;

    return (
        <Grid>
            <Grid.Row>
                <Grid.Column width={6}>
                    <SearchBar
                        search={queryParameters.search}
                        searchFcn={searchFunction}
                        cancelFcn={searchCancelFunction}
                        placeholder="Search Product"
                    />
                </Grid.Column>
                <Grid.Column width={6}>
                    <Dropdown 
                        selection
                        options={[
                            {
                                key: "all", value: "all", text: "All"
                            },
                            {
                                key: "monthly", value: "monthly", text: "Monthly"
                            },
                            {
                                key: "quarterly", value: "quarterly", text: "Quarterly"
                            },
                            {
                                key: "semi_annually", value: "semi-annually", text: "Semi-Annually"
                            },
                            {
                                key: "annually", value: "annually", text: "Annually"
                            }
                        ]}
                        selectOnBlur={false}
                        onChange={(_, { value }) => {
                            masterFilterField.onChange({ target: { name: masterFilterField.name, value: value === "all" ? null : value } })
                            const newQueryParameters = {
                                ...queryParameters,
                                master_filter: value === "all" ? null : value,
                                page: 1
                            };
                            setQueryParameters(newQueryParameters);
                            dispatch(productCatalogActions.readCatalog(newQueryParameters))
                        }}
                        value={masterFilterValue || "all"}
                        disabled={account_term && isUpsell && isDyl}
                    />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Ref innerRef={tabContainerRef}>
                    <Grid.Column>
                        <Tab
                            className="CatalogWizard__categories"
                            menu={{
                                secondary: true,
                                style: {
                                    maxWidth:
                                        tabContainerRef.current?.getBoundingClientRect()
                                            .width,
                                },
                            }}
                            panes={panes}
                            activeIndex={getActiveTab()}
                            onTabChange={(_, { activeIndex }) => {
                                onChangeTab(activeIndex);
                                const activeTab = panes[activeIndex]?.menuItem;
                                const selectedCategoryIndex = panes.findIndex(
                                    (pane) => pane.menuItem?.toLowerCase() === activeTab?.toLowerCase()
                                );
                                const product_category_id = (() => {
                                    if (selectedCategoryIndex === 0) {
                                        return null;
                                    }
                                    if (selectedCategoryIndex === 1) {
                                        return 0;
                                    }
                                    return panes[selectedCategoryIndex].key;
                                })();
                                const newQueryParameters = {
                                    ...queryParameters,
                                    product_category_id,
                                    page: 1
                                };
                                setQueryParameters(newQueryParameters);
                                dispatch(productCatalogActions.readCatalog(newQueryParameters));
                            }}
                        />
                    </Grid.Column>
                </Ref>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column>
                    <CategoryProducts
                        onChangePage={(page) => {
                            const newQueryParameters = {
                                ...queryParameters,
                                page,
                            };
                            setQueryParameters(newQueryParameters);
                            dispatch(productCatalogActions.readCatalog(newQueryParameters));
                        }}
                        hasFilters={
                            queryParameters.search ||
                            queryParameters.product_type
                        }
                        opportunity_id={opportunity_id}
                        queryParameters={queryParameters}
                        onFilter={(_, { name, value }) => {
                            const newQueryParameters = {
                                ...queryParameters,
                                [name]: value,
                                page: 1
                            };
                            setQueryParameters(newQueryParameters);
                            dispatch(productCatalogActions.readCatalog(newQueryParameters));
                        }}
                    />
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

export default Products;
